// client/src/pages/common/FirstLoginGate.js
// ============================================
// FIRST LOGIN GATE
// Forced password reset + Terms of Service / Privacy Policy
// acceptance for concierge-onboarded business accounts.
// ============================================

import React, { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { CURRENT_TERMS_VERSION, TERMS_URL, PRIVACY_URL } from '../../config/legal';
import PasswordField from '../../components/common/PasswordField';

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;

const roleDashboardPath = (role) => {
  if (role === 'wholesaler') return '/wholesaler/dashboard';
  if (role === 'admin') return '/admin/dashboard';
  return '/dashboard';
};

const FirstLoginGate = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { user, completeFirstLogin, logout } = useAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // If the user no longer needs onboarding, bounce them to their dashboard.
  if (user && !user.mustResetPassword && !user.mustAcceptTerms) {
    return <Navigate to={roleDashboardPath(user.role)} replace />;
  }

  const validate = () => {
    const next = {};
    if (!newPassword) {
      next.newPassword = t('firstLogin.errors.passwordRequired', 'New password is required');
    } else if (!PASSWORD_PATTERN.test(newPassword)) {
      next.newPassword = t(
        'firstLogin.errors.passwordWeak',
        'Password must be at least 8 chars with an uppercase, lowercase, and digit'
      );
    }
    if (newPassword !== confirmPassword) {
      next.confirmPassword = t('firstLogin.errors.passwordMismatch', 'Passwords do not match');
    }
    if (!acceptTerms) {
      next.acceptTerms = t('firstLogin.errors.mustAcceptTerms', 'You must accept the Terms of Service');
    }
    if (!acceptPrivacy) {
      next.acceptPrivacy = t('firstLogin.errors.mustAcceptPrivacy', 'You must accept the Privacy Policy');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    const result = await completeFirstLogin({
      newPassword,
      acceptTerms: true,
      termsVersion: CURRENT_TERMS_VERSION
    });
    setSubmitting(false);

    if (result.success) {
      toast.success(t('firstLogin.success', 'Welcome! Your account is ready.'));
      const updated = result.user || user;
      navigate(roleDashboardPath(updated?.role), { replace: true });
    } else {
      toast.error(result.error || t('firstLogin.errors.generic', 'Something went wrong'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('firstLogin.title', 'Welcome to WholesaleHub')}
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            {t('firstLogin.subtitle', 'Set a new password and accept our terms to continue.')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                {t('firstLogin.newPassword', 'New password')}
              </label>
              <PasswordField
                id="newPassword"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 py-2 border"
                disabled={submitting}
              />
              {errors.newPassword && (
                <p className="mt-1 text-xs text-red-600">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('firstLogin.confirmPassword', 'Confirm new password')}
              </label>
              <PasswordField
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 py-2 border"
                disabled={submitting}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  disabled={submitting}
                />
                <span>
                  {t('firstLogin.acceptTermsPrefix', 'I have read and accept the ')}
                  <Link to={TERMS_URL} target="_blank" rel="noreferrer" className="text-indigo-600 underline">
                    {t('firstLogin.termsLink', 'Terms of Service')}
                  </Link>
                </span>
              </label>
              {errors.acceptTerms && (
                <p className="text-xs text-red-600">{errors.acceptTerms}</p>
              )}

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  disabled={submitting}
                />
                <span>
                  {t('firstLogin.acceptPrivacyPrefix', 'I have read and accept the ')}
                  <Link to={PRIVACY_URL} target="_blank" rel="noreferrer" className="text-indigo-600 underline">
                    {t('firstLogin.privacyLink', 'Privacy Policy')}
                  </Link>
                </span>
              </label>
              {errors.acceptPrivacy && (
                <p className="text-xs text-red-600">{errors.acceptPrivacy}</p>
              )}
            </div>

            <div className="flex items-center justify-between pt-4">
              <button
                type="button"
                onClick={logout}
                className="text-sm text-gray-500 hover:text-gray-700"
                disabled={submitting}
              >
                {t('firstLogin.signOut', 'Sign out')}
              </button>
              <button
                type="submit"
                className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                disabled={submitting}
              >
                {submitting
                  ? t('firstLogin.submitting', 'Saving…')
                  : t('firstLogin.submit', 'Continue')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginGate;
